// The one place that talks to the server. Every request goes through this axios instance.
// (The live channel is the only exception: it uses EventSource, see lib/useLive.ts.)
import axios, { type AxiosError } from 'axios';
import type { ApiErrorBody } from '@serb/shared';

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    message: string,
  ) {
    super(message);
  }
}

const http = axios.create({
  withCredentials: true, // send the sign-in cookie
  headers: { 'Content-Type': 'application/json' },
  timeout: 30_000,
});

// Every failure becomes an ApiError carrying the server's plain-English message.
http.interceptors.response.use(undefined, (e: AxiosError<Partial<ApiErrorBody>>) => {
  const status = e.response?.status ?? 0;
  const message =
    e.response?.data?.error ??
    (e.response ? `Error ${status}` : 'Cannot reach the SERB server. Check your connection and try again.');
  return Promise.reject(new ApiError(status, message));
});

export const api = {
  get: <T>(url: string, params?: Record<string, string>) => http.get<T>(url, { params }).then((r) => r.data),
  post: <T>(url: string, body: unknown = {}) => http.post<T>(url, body).then((r) => r.data),
  /** send a file as it is (the name goes in the address) */
  upload: <T>(url: string, file: File, params: Record<string, string>) =>
    http
      .post<T>(url, file, { params, headers: { 'Content-Type': 'application/octet-stream' }, timeout: 120_000 })
      .then((r) => r.data),
  put: <T>(url: string, body: unknown = {}) => http.put<T>(url, body).then((r) => r.data),
};

/** Turn any thrown thing into a sentence for the screen */
export const errorText = (e: unknown, fallback = 'Something went wrong.') =>
  (e instanceof Error && e.message) || fallback;
